import { useState, type CSSProperties } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { CHART_FRAMEWORK_LABELS, formatCents, REPORT_BASES, REPORT_BASIS_LABELS, type FinanceReport, type ReportBasis, type ReportLine } from '@kontor/shared';
import { api, ApiError, queryString } from '../../api';
import { Bar, Donut, Legend, type Slice } from '../../components/charts';
import { InfoHint } from '../../components/InfoHint';
import { Modal } from '../../components/Modal';
import { invoiceDay } from './InvoicesPage';
import './chart.css';

const iso = (d: Date) => d.toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
const today = () => new Date(iso(new Date()) + 'T12:00:00');
const monthStart = (y: number, m: number) => iso(new Date(y, m, 1, 12));
const monthEnd = (y: number, m: number) => iso(new Date(y, m + 1, 0, 12));

/** Zeiträume zum Anklicken: Monat, Quartal, Jahr jeweils aktuell und davor */
export function presets(): { label: string; from: string; to: string }[] {
  const t = today();
  const y = t.getFullYear();
  const m = t.getMonth();
  const q = Math.floor(m / 3);
  const lastMonth = new Date(y, m - 1, 1, 12);
  const lastQuarterStart = q === 0 ? new Date(y - 1, 9, 1, 12) : new Date(y, (q - 1) * 3, 1, 12);
  return [
    { label: 'Dieser Monat', from: monthStart(y, m), to: monthEnd(y, m) },
    { label: 'Letzter Monat', from: monthStart(lastMonth.getFullYear(), lastMonth.getMonth()), to: monthEnd(lastMonth.getFullYear(), lastMonth.getMonth()) },
    { label: 'Dieses Quartal', from: monthStart(y, q * 3), to: monthEnd(y, q * 3 + 2) },
    { label: 'Letztes Quartal', from: monthStart(lastQuarterStart.getFullYear(), lastQuarterStart.getMonth()), to: monthEnd(lastQuarterStart.getFullYear(), lastQuarterStart.getMonth() + 2) },
    { label: 'Dieses Jahr', from: `${y}-01-01`, to: `${y}-12-31` },
    { label: 'Letztes Jahr', from: `${y - 1}-01-01`, to: `${y - 1}-12-31` },
  ];
}

/** So viele Konten zeigt ein Kreisdiagramm einzeln, der Rest wird zu „Weitere“ zusammengefasst und steht in der ganzen Liste */
const TOP = 5;

type Open = null | 'revenue' | 'expense' | 'notes';

/**
 * Finanzreport (KON-162): Einnahmen-Überschuss mit Konten für einen frei gewählten Zeitraum, als Bildschirmauswertung sowie als CSV (Excel) und PDF zum Herunterladen.
 * Gebucht wird in Wirkia nicht, die Zahlen kommen aus Rechnungen und Zahlungen. Die Zuordnung (Rechnungs- oder Zahlungsdatum) folgt der Versteuerungsart, lässt sich aber wechseln.
 * Die Seite ist nach Wichtigkeit gebaut: oben das Ergebnis, darunter die Verteilung von Erlösen und Aufwand, zuletzt Umsatzsteuer und Hinweise. Die ganzen Kontenlisten öffnen in einem Fenster.
 */
export function ReportPage() {
  const list = presets();
  const [from, setFrom] = useState(list[4]!.from);
  const [to, setTo] = useState(list[4]!.to);
  const [basis, setBasis] = useState<'' | ReportBasis>('');
  const [open, setOpen] = useState<Open>(null);
  const query = { from, to, basis };
  const valid = !!from && !!to && to >= from;

  const report = useQuery({
    queryKey: ['finance-report', query],
    queryFn: async () => (await api<{ data: FinanceReport }>('GET', `/chart/report${queryString({ ...query, format: 'json' })}`)).data,
    enabled: valid,
    placeholderData: (previous) => previous,
  });
  const r = report.data;
  const link = (format: 'csv' | 'pdf') => `/api/v1/chart/report${queryString({ ...query, format })}`;

  return (
    <>
      <div className="page-head">
        <h1>Finanzreport</h1>
        <span className="muted">Einnahmen-Überschuss mit Konten</span>
      </div>

      <section className="card rp-bar" aria-label="Zeitraum">
        <div className="rp-bar__top">
          <div className="report-presets" role="group" aria-label="Zeitraum wählen">
            {list.map((p) => (
              <button key={p.label} type="button" className={`btn btn--sm ${p.from === from && p.to === to ? 'btn--primary' : 'btn--secondary'}`} onClick={() => { setFrom(p.from); setTo(p.to); }}>{p.label}</button>
            ))}
          </div>
          <div className="rp-bar__downloads">
            <a className={`btn btn--secondary btn--sm${valid ? '' : ' is-disabled'}`} href={valid ? link('csv') : undefined} aria-disabled={!valid}>CSV (Excel)</a>
            <a className={`btn btn--secondary btn--sm${valid ? '' : ' is-disabled'}`} href={valid ? link('pdf') : undefined} aria-disabled={!valid}>PDF</a>
          </div>
        </div>
        <div className="rp-bar__fields">
          <label className="field">
            <span>Von</span>
            <input className="input" type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="field">
            <span>Bis</span>
            <input className="input" type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} aria-invalid={!valid && !!to} />
            {!valid && !!to && !!from && <small className="field__error" role="alert">Das Ende liegt vor dem Beginn.</small>}
          </label>
          <label className="field">
            <span>Zuordnung<InfoHint label="Zuordnung">Nach Rechnungsdatum zählt eine Rechnung in dem Zeitraum, in dem sie ausgestellt wurde (Sollversteuerung). Nach Zahlungsdatum zählt jede Zahlung anteilig in dem Zeitraum, in dem sie einging oder ausging (Istversteuerung). Ohne Auswahl folgt der Report der Versteuerungsart aus Einrichtung → Finanzen.</InfoHint></span>
            <select className="input" value={basis} onChange={(e) => setBasis(e.target.value as '' | ReportBasis)}>
              <option value="">Wie in der Einrichtung</option>
              {REPORT_BASES.map((b) => <option key={b} value={b}>{REPORT_BASIS_LABELS[b]}</option>)}
            </select>
          </label>
        </div>
      </section>

      {report.isLoading && <p className="muted">Wird berechnet …</p>}
      {report.isError && <div className="alert alert--error" role="alert">{report.error instanceof ApiError ? (Object.values(report.error.fields)[0] ?? report.error.message) : 'Der Report konnte nicht erstellt werden.'}</div>}

      {r && (
        <>
          <p className="rp-meta muted small">
            <span>{invoiceDay(r.from)} bis {invoiceDay(r.to)} · {REPORT_BASIS_LABELS[r.basis]} · {CHART_FRAMEWORK_LABELS[r.framework]}</span>
            {r.notes.length > 0 && (
              <button type="button" className="rp-notes-btn" onClick={() => setOpen('notes')}>
                {r.notes.length === 1 ? '1 Hinweis' : `${r.notes.length} Hinweise`}
              </button>
            )}
          </p>

          <div className="report-tiles">
            <Tile label="Einnahmen netto" value={formatCents(r.totals.revenueNetCents)} color={2} />
            <Tile label="Ausgaben netto" value={formatCents(r.totals.expenseNetCents)} color={7} />
            <Tile label={r.totals.resultCents >= 0 ? 'Überschuss' : 'Fehlbetrag'} value={formatCents(r.totals.resultCents)} strong tone={r.totals.resultCents >= 0 ? 'good' : 'bad'} color={r.totals.resultCents >= 0 ? 2 : 7} />
            <Tile label={r.totals.vatPayableCents >= 0 ? 'Umsatzsteuer-Zahllast' : 'Vorsteuer-Erstattung'} value={formatCents(Math.abs(r.totals.vatPayableCents))} color={3} />
          </div>

          <div className="rp-grid">
            <Distribution
              title="Erlöse"
              lines={r.revenue}
              sum={r.totals.revenueNetCents}
              onAll={() => setOpen('revenue')}
            />
            <Distribution
              title="Aufwand"
              lines={r.expenses}
              sum={r.totals.expenseNetCents}
              onAll={() => setOpen('expense')}
            />
          </div>

          <div className="rp-grid rp-grid--foot">
            <section className="card rp-card" aria-label="Einnahmen und Ausgaben">
              <h2 className="rp-card__title">Einnahmen und Ausgaben</h2>
              <div className="rp-compare">
                <span>Einnahmen</span>
                <Bar value={r.totals.revenueNetCents} max={Math.max(r.totals.revenueNetCents, r.totals.expenseNetCents)} color={2} />
                <strong>{formatCents(r.totals.revenueNetCents)}</strong>
                <span>Ausgaben</span>
                <Bar value={r.totals.expenseNetCents} max={Math.max(r.totals.revenueNetCents, r.totals.expenseNetCents)} color={7} />
                <strong>{formatCents(r.totals.expenseNetCents)}</strong>
              </div>
              <p className="muted small rp-card__note">
                {r.totals.revenueNetCents > 0 ? `Von 100 € Einnahmen bleiben ${Math.round((r.totals.resultCents / r.totals.revenueNetCents) * 100)} € als ${r.totals.resultCents >= 0 ? 'Überschuss' : 'Fehlbetrag'} (netto).` : 'Im Zeitraum gibt es keine Einnahmen.'}
              </p>
            </section>

            <section className="card rp-card" aria-label="Umsatzsteuer">
              <h2 className="rp-card__title">Umsatzsteuer</h2>
              <div className="rp-compare">
                <span>Auf Erlöse</span>
                <Bar value={r.totals.outputTaxCents} max={Math.max(r.totals.outputTaxCents, r.totals.inputTaxCents)} color={1} />
                <strong>{formatCents(r.totals.outputTaxCents)}</strong>
                <span>Vorsteuer</span>
                <Bar value={r.totals.inputTaxCents} max={Math.max(r.totals.outputTaxCents, r.totals.inputTaxCents)} color={4} />
                <strong>{formatCents(r.totals.inputTaxCents)}</strong>
              </div>
              <p className="rp-card__note">
                <strong>{r.totals.vatPayableCents >= 0 ? 'Zahllast an das Finanzamt: ' : 'Erstattung vom Finanzamt: '}{formatCents(Math.abs(r.totals.vatPayableCents))}</strong>
              </p>
            </section>
          </div>

          <Modal open={open === 'revenue'} title="Erlöse nach Konto" wide onClose={() => setOpen(null)}>
            <ReportTable title="Erlöse" lines={r.revenue} sum={r.totals.revenueNetCents} kind="revenue" from={r.from} to={r.to} basis={r.basis} />
          </Modal>
          <Modal open={open === 'expense'} title="Aufwand nach Konto" wide onClose={() => setOpen(null)}>
            <ReportTable title="Aufwand" lines={r.expenses} sum={r.totals.expenseNetCents} kind="expense" from={r.from} to={r.to} basis={r.basis} />
          </Modal>
          <Modal open={open === 'notes'} title="Hinweise zum Report" onClose={() => setOpen(null)}>
            <ul className="chart-hints">{r.notes.map((n) => <li key={n}>{n}</li>)}</ul>
          </Modal>
        </>
      )}
    </>
  );
}

function Tile({ label, value, strong, tone, color }: { label: string; value: string; strong?: boolean; tone?: 'good' | 'bad'; color: number }) {
  return (
    <div className={`report-tile${tone ? ` report-tile--${tone}` : ''}`} style={{ '--tile': `var(--chart-${color})` } as CSSProperties}>
      <span className="muted small">{label}</span>
      {strong ? <strong>{value}</strong> : <span className="report-tile__value">{value}</span>}
    </div>
  );
}

/** Kreisdiagramm mit Legende für die größten Konten. Die Schaltfläche in der Ecke öffnet die ganze Liste. */
function Distribution({ title, lines, sum, onAll }: { title: string; lines: ReportLine[]; sum: number; onAll: () => void }) {
  const [active, setActive] = useState<string | null>(null);
  const sorted = [...lines].filter((l) => l.netCents > 0).sort((a, b) => b.netCents - a.netCents);
  const slices: Slice[] = sorted.slice(0, TOP).map((l) => ({ key: l.accountNumber ?? 'ohne', label: l.accountName, value: l.netCents }));
  const rest = sorted.slice(TOP);
  if (rest.length > 0) slices.push({ key: 'rest', label: `Weitere (${rest.length})`, value: rest.reduce((s, l) => s + l.netCents, 0) });

  return (
    <section className="card rp-card" aria-label={title}>
      <div className="rp-card__head">
        <h2 className="rp-card__title">{title}</h2>
        <button type="button" className="btn btn--secondary btn--sm rp-card__all" onClick={onAll} disabled={lines.length === 0} aria-label={`${title}: alle ${lines.length} Konten anzeigen`}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
          </svg>
          Alle {lines.length}
        </button>
      </div>
      {slices.length === 0 ? (
        <p className="empty">Keine Belege in diesem Zeitraum.</p>
      ) : (
        <div className="rp-dist">
          <Donut slices={slices} active={active} onActive={setActive} title={title} total={formatCents(sum)} format={formatCents} ariaLabel={`${title} nach Konto, zusammen ${formatCents(sum)}`} />
          <Legend slices={slices} active={active} onActive={setActive} format={formatCents} />
        </div>
      )}
    </section>
  );
}

function ReportTable({ title, lines, sum, kind, from, to, basis }: { title: string; lines: ReportLine[]; sum: number; kind: 'revenue' | 'expense'; from: string; to: string; basis: string }) {
  /** Sprung zu den Sachposten dieses Kontos im selben Zeitraum und mit derselben Zuordnung */
  const postings = (account: string | null) => `/sachposten${queryString({ from, to, basis, account: account ?? 'none', kind })}`;
  return (
    <table className="table table--cards">
      <thead>
        <tr>
          <th>Konto</th>
          <th>Bezeichnung</th>
          <th>Kategorie</th>
          <th className="num">Netto</th>
          <th className="num">Steuer</th>
          <th className="num">Brutto</th>
          <th className="num">Belege</th>
        </tr>
      </thead>
      <tbody>
        {lines.length === 0 && <tr><td colSpan={7} className="empty">Keine Belege in diesem Zeitraum.</td></tr>}
        {lines.map((l) => (
          <tr key={l.accountNumber ?? 'ohne'} className={l.accountNumber ? undefined : 'row--muted'}>
            <td data-label="Konto">{l.accountNumber ?? '–'}</td>
            <td data-label="Bezeichnung"><Link to={postings(l.accountNumber)} className="strong-link" title="Die einzelnen Posten dieses Kontos anzeigen">{l.accountName}</Link></td>
            <td data-label="Kategorie" className="muted small">{l.categoryPath || '–'}</td>
            <td data-label="Netto" className="num">{formatCents(l.netCents)}</td>
            <td data-label="Steuer" className="num">{formatCents(l.taxCents)}</td>
            <td data-label="Brutto" className="num">{formatCents(l.grossCents)}</td>
            <td data-label="Belege" className="num">{l.count}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <th colSpan={3}>Summe {title}</th>
          <th className="num">{formatCents(sum)}</th>
          <th colSpan={3} />
        </tr>
      </tfoot>
    </table>
  );
}
